import { createServerFn } from '@tanstack/react-start';
import { env } from 'cloudflare:workers';
import { eq } from 'drizzle-orm';
import { nanoid } from 'nanoid';
import { z } from 'zod';
import {
  VideoAspectRatio,
  VideoGenerationMode,
  VideoGenerationModel,
  VideoResolution,
} from '@/ai/video-models';
import {
  buildTrendPrompt,
  getTrend,
  getTrendCredits,
  resolveTrendVariant,
  type TrendSceneChoice,
} from '@/config/trends';
import { getUserCredits } from '@/credits/credits';
import { getDb } from '@/db';
import { videoGeneration } from '@/db/app.schema';
import { getBaseUrl } from '@/lib/urls';
import { authApiMiddleware } from '@/middlewares/auth-middleware';
import type {
  VideoFrameReference,
  VideoGenerationParams,
} from '@/workflows/video-generation';
import { INSUFFICIENT_VIDEO_CREDITS_ERROR } from './video-generate';
import { resolveVideoReferenceFiles } from './video-reference-files-server';

const MODEL = VideoGenerationModel.SEEDANCE_2_5;

const trendVideoInputSchema = z.object({
  trendId: z.string().min(1).max(64),
  /** Template of a multi-template effect; omitted for single-template ones. */
  variantId: z.string().min(1).max(32).optional(),
  photoFileIds: z.array(z.string().min(1)).min(1).max(4),
  scene: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('original') }),
    z.object({ kind: z.literal('preset'), sceneId: z.string().min(1) }),
    z.object({ kind: z.literal('upload'), fileId: z.string().min(1) }),
  ]),
  resolution: z.enum([VideoResolution.P480, VideoResolution.P720]),
});

export const generateTrendVideo = createServerFn({ method: 'POST' })
  .inputValidator(trendVideoInputSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const baseTrend = getTrend(data.trendId);
    if (!baseTrend) throw new Error('Unknown effect.');
    const trend = resolveTrendVariant(baseTrend, data.variantId);
    if (!trend) throw new Error('Unknown effect version.');
    const variant = trend.variants?.items.find(
      (item) => item.id === (data.variantId ?? trend.variants?.defaultId)
    );
    const variantLabel = variant?.label;
    if (data.photoFileIds.length !== trend.photoSlots.length) {
      throw new Error(
        `This effect needs ${trend.photoSlots.length} ${
          trend.photoSlots.length === 1 ? 'photo' : 'photos'
        }.`
      );
    }
    if (!trend.resolutions.includes(data.resolution)) {
      throw new Error(`This effect does not support ${data.resolution}.`);
    }
    if (data.scene.kind !== 'original' && !trend.scenes) {
      throw new Error('This effect keeps its original background.');
    }

    const preset =
      data.scene.kind === 'preset'
        ? trend.scenes?.items.find(
            (item) =>
              data.scene.kind === 'preset' && item.id === data.scene.sceneId
          )
        : undefined;
    if (data.scene.kind === 'preset' && !preset) {
      throw new Error('Unknown background.');
    }

    const template = await env.BUCKET.head(trend.templateKey);
    if (!template) {
      throw new Error('This effect is not available yet. Try again later.');
    }

    const imageFileIds =
      data.scene.kind === 'upload'
        ? [...data.photoFileIds, data.scene.fileId]
        : data.photoFileIds;
    const referenceImages = await resolveVideoReferenceFiles({
      userId,
      referenceFileIds: imageFileIds,
    });
    const templateVideo: VideoFrameReference = {
      fileId: variant ? `trend:${trend.id}:${variant.id}` : `trend:${trend.id}`,
      r2Key: trend.templateKey,
      contentType: template.httpMetadata?.contentType ?? 'video/mp4',
    };
    const baseUrl = getBaseUrl();
    const publicReferenceImageUrls = preset
      ? [`${baseUrl.replace(/\/$/, '')}${preset.image}`]
      : [];

    const sceneChoice: TrendSceneChoice =
      data.scene.kind === 'preset'
        ? { kind: 'preset', sceneId: data.scene.sceneId }
        : { kind: data.scene.kind };
    const prompt = buildTrendPrompt(trend, sceneChoice);
    const duration = trend.templateSeconds;
    const cost = getTrendCredits(trend, data.resolution);

    const balance = await getUserCredits(userId);
    if (balance.balance < cost) {
      throw new Error(
        `${INSUFFICIENT_VIDEO_CREDITS_ERROR}:${cost}:${balance.balance}`
      );
    }

    const videoId = nanoid();
    const description = variantLabel
      ? `${trend.name} video, ${variantLabel} (${data.resolution})`
      : `${trend.name} video (${data.resolution})`;
    const db = getDb();
    await db.insert(videoGeneration).values({
      id: videoId,
      userId,
      type: 'reference-to-video',
      prompt,
      model: MODEL,
      aspectRatio: VideoAspectRatio.ADAPTIVE,
      duration,
      resolution: data.resolution,
      mode: VideoGenerationMode.NORMAL,
      startFrameFileId: referenceImages[0]?.fileId ?? null,
      referenceFileIds: JSON.stringify(imageFileIds),
      templateId: trend.id,
      generateAudio: false,
      creditsUsed: cost,
      status: 'pending',
      provider: 'kie',
      createdAt: new Date(),
      errorMessage: null,
    });

    let instanceId: string;
    try {
      const params: VideoGenerationParams = {
        userId,
        videoId,
        prompt,
        model: MODEL,
        aspectRatio: VideoAspectRatio.ADAPTIVE,
        duration,
        resolution: data.resolution,
        mode: VideoGenerationMode.NORMAL,
        generateAudio: false,
        workflow: 'reference-to-video',
        referenceImages,
        referenceVideos: [templateVideo],
        publicReferenceImageUrls,
        creditsUsed: cost,
        description,
        provider: 'kie',
        baseUrl,
      };
      const instance = await env.VIDEO_GEN_WORKFLOW.create({ params });
      instanceId = instance.id;
      await db
        .update(videoGeneration)
        .set({ workflowInstanceId: instanceId })
        .where(eq(videoGeneration.id, videoId));
    } catch (err) {
      await db.delete(videoGeneration).where(eq(videoGeneration.id, videoId));
      throw err instanceof Error
        ? err
        : new Error('Failed to start video generation.');
    }

    return {
      videoId,
      workflowInstanceId: instanceId,
      duration,
      resolution: data.resolution,
      creditsCharged: cost,
      createdAt: Date.now(),
    };
  });
